import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppBooking from '../components/WhatsAppBooking';
import ChakraDiagram from './components/ChakraDiagram';
import SEOJsonLd from '../../../components/base/SEOJsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thesunshinehealing.com';

const studioImage = 'https://public.readdy.ai/ai/img_res/561a5639c2d8c3dc8e54df329358952e.jpg spiritual healing studio banner design with glowing lotus flowers chakra rainbow energy light sacred geometry golden patterns serene meditation space white and gold cream color palette peaceful divine atmosphere ethereal glow natural elements Singapore wellness brand beautiful harmonious spiritual landscape with soft light rays and healing crystals&width=1200&height=630&seq=studio-og-preview-001&orientation=landscape';

const chakraJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Chakra Assessment Singapore — 7 Chakra Energy Evaluation & Healing 脉轮测试',
    description:
      'Professional Chakra Assessment in Singapore. Systematically evaluate all 7 chakra energy centres, identify blockages and receive personalized healing recommendations and plan. Sessions from SGD $30.',
    url: `${siteUrl}/services/chakra`,
    image: studioImage,
    areaServed: 'Singapore',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Sunshine Healing Studio',
      url: siteUrl,
      telephone: '+6593651286',
      address: { '@type': 'PostalAddress', addressLocality: 'Singapore', addressCountry: 'SG' },
    },
    offers: [
      { '@type': 'Offer', price: '30', priceCurrency: 'SGD', description: 'Quick Chakra Assessment (15 mins)' },
      { '@type': 'Offer', price: '60', priceCurrency: 'SGD', description: 'In-Depth Chakra Reading (30 mins)' },
      { '@type': 'Offer', price: '100', priceCurrency: 'SGD', description: 'Chakra Assessment + Initial Healing Guidance (60 mins)' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页 Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: '脉轮测试 Chakra Assessment', item: `${siteUrl}/services/chakra` },
    ],
  },
];

const CHAKRA_COLORS = [
  'bg-rose-600',
  'bg-orange-500',
  'bg-yellow-400',
  'bg-emerald-500',
  'bg-sky-400',
  'bg-indigo-400',
  'bg-violet-400',
];

export default function ChakraPage() {
  const { t } = useTranslation('common');

  const chakraItems = t('chakraDetail.chakras.items', { returnObjects: true }) as {
    name: string;
    color: string;
    description: string;
  }[];

  const pricingItems = t('chakraDetail.pricing.items', { returnObjects: true }) as {
    duration: string;
    price: string;
    label: string;
  }[];

  return (
    <div className="min-h-screen bg-white">
      <title>脉轮测试 新加坡 | Chakra Assessment Singapore — 7 Chakra Energy Evaluation & Healing</title>
      <meta name="description" content="新加坡专业脉轮测试，系统评估七大脉轮能量状态，精准定位能量阻塞与失衡点，提供个性化疗愈方案。15-60分钟，SGD $30起。Singapore Chakra Assessment — 7 chakra energy evaluation, identify blockages, personalized healing plan." />
      <meta name="keywords" content="chakra assessment Singapore, chakra healing Singapore, 7 chakra evaluation, energy healing Singapore, chakra balancing" />
      <link rel="canonical" href={`${siteUrl}/services/chakra`} />
      <meta property="og:title" content="脉轮测试 新加坡 | Chakra Assessment Singapore" />
      <meta property="og:description" content="系统评估七大脉轮能量状态，精准定位阻塞与失衡，提供个性化疗愈方案。新加坡专业脉轮测试与疗愈。" />
      <meta property="og:url" content={`${siteUrl}/services/chakra`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={studioImage} />
      <SEOJsonLd id="chakra-jsonld" data={chakraJsonLd} />
      <Navbar />

      {/* Hero Banner */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center overflow-hidden pt-16 md:pt-0">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=seven%20chakra%20energy%20centers%20glowing%20along%20human%20silhouette%20in%20deep%20meditation%2C%20colorful%20rainbow%20spectrum%20of%20light%20from%20red%20at%20base%20to%20violet%20at%20crown%2C%20each%20energy%20point%20radiating%20vibrant%20sacred%20geometry%20light%2C%20ethereal%20cosmic%20background%20with%20soft%20mist%20and%20golden%20particles%2C%20spiritual%20awakening%20visualization%2C%20serene%20transcendent%20atmosphere&width=1920&height=1080&seq=chakra-hero-bg-001&orientation=landscape"
            alt="Chakra Assessment"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/65 via-stone-900/45 to-stone-900/65"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-6 text-center">
          <div className="inline-block px-4 py-1.5 bg-rose-500/20 backdrop-blur-sm rounded-full border border-rose-400/30 mb-4 md:mb-5">
            <span className="text-rose-300 text-xs md:text-sm font-medium tracking-widest">SPIRITUAL HEALING</span>
          </div>
          <h1 className="text-3xl md:text-6xl font-bold text-white mb-4 md:mb-5 tracking-tight">
            {t('chakraDetail.hero.title')}
          </h1>
          <p className="text-base md:text-xl text-stone-200 leading-relaxed">
            {t('chakraDetail.hero.description')}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:grid md:grid-cols-2 md:gap-16 md:items-center gap-8">
            <div>
              <p className="text-rose-500 text-xs md:text-sm font-semibold tracking-widest uppercase mb-2 md:mb-3">Introduction</p>
              <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4 md:mb-6 leading-snug">
                {t('chakraDetail.intro.title')}
              </h2>
              <p className="text-stone-600 leading-relaxed text-sm md:text-base">
                {t('chakraDetail.intro.content')}
              </p>
              <div className="mt-6 md:mt-10 grid grid-cols-2 gap-3 md:gap-4">
                {[
                  { icon: 'ri-scan-line', key: 'benefit1' },
                  { icon: 'ri-eye-line', key: 'benefit2' },
                  { icon: 'ri-focus-line', key: 'benefit3' },
                  { icon: 'ri-contrast-drop-line', key: 'benefit4' },
                ].map((item) => (
                  <div key={item.key} className="flex items-center gap-2 md:gap-3 bg-rose-50 rounded-xl px-3 md:px-4 py-2.5 md:py-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-rose-500 flex-shrink-0">
                      <i className={`${item.icon} text-base md:text-lg`}></i>
                    </div>
                    <span className="text-stone-700 text-xs md:text-sm font-medium">
                      {t(`chakraDetail.benefits.${item.key}.title`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative w-full h-64 md:h-[520px] rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=person%20sitting%20in%20peaceful%20meditation%20with%20hands%20on%20lap%2C%20transparent%20body%20showing%20glowing%20chakra%20points%20along%20spine%20from%20root%20to%20crown%20each%20emitting%20soft%20colorful%20light%2C%20serene%20nature%20setting%20with%20soft%20morning%20sunlight%20filtering%20through%20trees%2C%20tranquil%20spiritual%20atmosphere%20with%20warm%20golden%20haze%20and%20gentle%20bokeh%20background&width=800&height=1000&seq=chakra-intro-img-001&orientation=portrait"
                alt="Chakra Meditation"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Chakra Position Diagram */}
      <ChakraDiagram />

      {/* Seven Chakras Visual */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-rose-100 text-rose-500">
                <i className="ri-flashlight-line text-lg"></i>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('chakraDetail.chakras.title')}</h3>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 md:gap-4">
            {Array.isArray(chakraItems) && chakraItems.map((chakra, i) => (
              <div
                key={i}
                className="flex items-center gap-4 md:gap-6 bg-white rounded-xl px-5 md:px-7 py-4 md:py-5 border border-stone-100 hover:border-rose-200 transition-colors"
              >
                <div className={`w-10 h-10 md:w-12 md:h-12 flex-shrink-0 rounded-full ${CHAKRA_COLORS[i]} flex items-center justify-center`}>
                  <span className="text-white font-bold text-sm md:text-base">{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-stone-800 text-sm md:text-base">{chakra.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-stone-100 text-stone-500 rounded-full">{chakra.color}</span>
                  </div>
                  <p className="text-stone-500 text-xs md:text-sm leading-relaxed">{chakra.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Suitable For & Method */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-rose-50/50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:grid md:grid-cols-2 md:gap-16 gap-10">
            {/* 适合人群 */}
            <div>
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-rose-100 text-rose-500">
                  <i className="ri-user-heart-line text-lg"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('chakraDetail.suitable.title')}</h3>
              </div>
              <ul className="space-y-3">
                {[
                  t('chakraDetail.suitable.item1'),
                  t('chakraDetail.suitable.item2'),
                  t('chakraDetail.suitable.item3'),
                  t('chakraDetail.suitable.item4'),
                  t('chakraDetail.suitable.item5'),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone-600 text-sm md:text-base">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-rose-400 text-base"></i>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* 测试方式 */}
            <div>
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-rose-100 text-rose-500">
                  <i className="ri-magic-line text-lg"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('chakraDetail.method.title')}</h3>
              </div>
              <div className="space-y-4">
                {[
                  { icon: 'ri-file-list-3-line', step: 'step1' },
                  { icon: 'ri-search-eye-line', step: 'step2' },
                  { icon: 'ri-bar-chart-grouped-line', step: 'step3' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-white rounded-xl border border-rose-100">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-rose-100 text-rose-500 flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 text-sm md:text-base mb-1">
                        {t(`chakraDetail.method.${item.step}.title`)}
                      </p>
                      <p className="text-stone-500 text-xs md:text-sm leading-relaxed">
                        {t(`chakraDetail.method.${item.step}.desc`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-rose-100 text-rose-500">
              <i className="ri-price-tag-3-line text-lg"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('chakraDetail.pricing.title')}</h3>
          </div>
          <p className="text-stone-400 text-sm mb-8">{t('chakraDetail.pricing.note')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {Array.isArray(pricingItems) && pricingItems.map((item, i) => (
              <div key={i} className="flex-1 max-w-xs mx-auto sm:mx-0 bg-rose-50 border border-rose-100 rounded-2xl px-8 py-7 flex flex-col items-center gap-2">
                <span className="text-xs font-semibold tracking-widest text-rose-400 uppercase mb-1">{item.label}</span>
                <div className="flex items-center gap-2 text-stone-700">
                  <div className="w-5 h-5 flex items-center justify-center text-rose-400">
                    <i className="ri-time-line text-base"></i>
                  </div>
                  <span className="text-base font-medium">{item.duration}</span>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-rose-500 mt-1">{item.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking */}
      <WhatsAppBooking accentColor="rose" serviceName="脉轮测试" />

      {/* Back */}
      <section className="py-8 md:py-10 px-4 md:px-6 text-center bg-white">
        <Link
          to="/#services"
          className="inline-flex items-center gap-2 px-6 md:px-7 py-3 border border-stone-300 text-stone-700 rounded-full hover:bg-stone-100 transition-colors duration-300 text-sm whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span>{t('chakraDetail.cta.back')}</span>
        </Link>
      </section>

      <Footer />
    </div>
  );
}
